
export interface AikenParseStats {
  totalLines: number;
  validQuestions: number;
  invalidRows: number;
  skippedReasons: Record<string, number>;
  skippedRows: {rowIndex: number, reason: string}[];
}

export interface ParsedQuestion {
  exam_name: string;
  exam_category: string;
  subject: string;
  category: string;
  question_type: string;
  question_text: string;
  option_a: string;
  option_b: string;
  option_c: string;
  option_d: string;
  option_e?: string; // Added optional option_e
  correct_option: string;
  difficulty: string;
  metadata: {
    original_section: string | null;
    set_name: string | null;
    question_no: number;
    passage?: string;
  };
}

export interface AikenParseResult {
  questions: ParsedQuestion[];
  stats: AikenParseStats;
}

export const parseAikenFormat = (
  content: string, 
  examDetails: {
    examName: string;
    subject: string;
    setName: string;
  },
  selectedExam?: {
    name: string;
    type: string;
  },
  selectedSection?: {
    name: string;
  }
): AikenParseResult => {
  console.log("Parsing content:", content.substring(0, 200) + "...");
  
  // Initialize stats
  const stats: AikenParseStats = {
    totalLines: content.split('\n').length,
    validQuestions: 0,
    invalidRows: 0,
    skippedReasons: {},
    skippedRows: []
  };
  
  // Split content into blocks separated by at least one blank line
  const blocks = content.split(/\n\s*\n/).filter(block => block.trim());
  
  const questions: ParsedQuestion[] = [];
  
  // Keep track of the current passage
  let currentPassage: string | null = null;
  let previousQuestionWithPassage: string | null = null;
  
  blocks.forEach((block, blockIndex) => {
    try {
      const lines = block.split('\n').map(line => line.trim()).filter(line => line);
      
      if (lines.length < 5) { // Minimum: question + 4 options + answer
        const reason = 'Not enough lines for a complete question';
        stats.skippedReasons[reason] = (stats.skippedReasons[reason] || 0) + 1;
        stats.skippedRows.push({ rowIndex: blockIndex, reason });
        stats.invalidRows++;
        return;
      }
      
      // Check if this is a passage
      // Identify passages by checking if the block doesn't contain option patterns and ANSWER:
      const hasOptionPattern = lines.some(line => /^[A-E][.):]\s+.+/i.test(line));
      const hasAnswerLine = lines.some(line => line.toUpperCase().startsWith('ANSWER:'));
      
      const isPassage = lines.length > 5 && 
                        !hasOptionPattern && 
                        !hasAnswerLine;
      
      if (isPassage) {
        currentPassage = block;
        return; // Skip processing this block as a question
      }
      
      // Extract the question text (everything before the first option)
      let questionText = '';
      let optionStartIndex = -1;
      
      for (let i = 0; i < lines.length; i++) {
        // Check for line that starts with A., A:, A), A-, etc. (now supporting A-E)
        if (/^[A-E][.):]\s+.+/i.test(lines[i])) {
          optionStartIndex = i;
          break;
        }
      }
      
      if (optionStartIndex === -1) {
        const reason = 'Could not identify the first option';
        stats.skippedReasons[reason] = (stats.skippedReasons[reason] || 0) + 1;
        stats.skippedRows.push({ rowIndex: blockIndex, reason });
        stats.invalidRows++;
        return;
      }
      
      // Question is all lines before the options
      questionText = lines.slice(0, optionStartIndex).join('\n');
      
      // Detect if this question refers to the same passage as a previous question
      // by checking if the first line contains numbered items or words like "according to the passage"
      const refersToPassage = 
        /^(\d+\.|\(\d+\))/.test(questionText) || // Numbered items
        /(?:according|refer|passage|above|following)/i.test(questionText);
      
      // If the first line has a numbered format (like "1. Senior citizens...") 
      // and we have a passage stored, this is likely part of a passage-based question set
      const isQuestionWithPassage = !!currentPassage && refersToPassage;
      
      // Process options
      const options: Record<string, string> = {};
      let answerLine = '';
      let answerIndex = -1;
      
      for (let i = optionStartIndex; i < lines.length; i++) {
        const line = lines[i];
        
        // Check for ANSWER: line
        if (line.toUpperCase().startsWith('ANSWER:')) {
          answerLine = line;
          answerIndex = i;
          break;
        }
        
        // Match options with various formats: A., A:, A), A-, etc. (now supporting A-E)
        const optionMatch = line.match(/^([A-E])[.):]\s*(.+)/i);
        if (optionMatch) {
          const [, letter, text] = optionMatch;
          options[letter.toUpperCase()] = text.trim();
        }
      }
      
      if (answerIndex === -1) {
        const reason = 'No ANSWER line found';
        stats.skippedReasons[reason] = (stats.skippedReasons[reason] || 0) + 1;
        stats.skippedRows.push({ rowIndex: blockIndex, reason });
        stats.invalidRows++;
        return;
      }
      
      // Extract the answer letter
      const answerMatch = answerLine.match(/ANSWER:\s*([A-E])/i);
      if (!answerMatch) {
        const reason = `Invalid answer format: "${answerLine}"`;
        stats.skippedReasons[reason] = (stats.skippedReasons[reason] || 0) + 1;
        stats.skippedRows.push({ rowIndex: blockIndex, reason });
        stats.invalidRows++;
        return;
      }
      
      const answer = answerMatch[1].toUpperCase();
      
      // Validate that options A-D are present (required)
      const requiredOptions = ['A', 'B', 'C', 'D'];
      const missingOptions = requiredOptions.filter(opt => !options[opt]);
      
      if (missingOptions.length > 0) {
        const reason = `Missing required options: ${missingOptions.join(', ')}`;
        stats.skippedReasons[reason] = (stats.skippedReasons[reason] || 0) + 1;
        stats.skippedRows.push({ rowIndex: blockIndex, reason });
        stats.invalidRows++;
        return;
      }
      
      // Validate the answer is one of the available options
      if (!options[answer]) {
        const reason = `Answer "${answer}" does not match any of the available options`;
        stats.skippedReasons[reason] = (stats.skippedReasons[reason] || 0) + 1;
        stats.skippedRows.push({ rowIndex: blockIndex, reason });
        stats.invalidRows++;
        return;
      }
      
      // Create the metadata
      const metadata: ParsedQuestion['metadata'] = {
        original_section: selectedSection?.name || null,
        set_name: examDetails.setName || null,
        question_no: questions.length + 1,
      };
      
      // If this question refers to a passage, include the passage
      if (isQuestionWithPassage) {
        metadata.passage = currentPassage;
        previousQuestionWithPassage = questionText;
      } 
      // If this question seems related to a previous question with a passage
      else if (currentPassage && previousQuestionWithPassage && 
              questionText.length < 100 && !questionText.includes('.')) {
        metadata.passage = currentPassage;
      }
      
      // Create the parsed question with option_e if it exists
      const parsedQuestion: ParsedQuestion = {
        exam_name: examDetails.examName,
        exam_category: selectedExam?.type || 'General',
        subject: examDetails.subject,
        category: examDetails.subject,
        question_type: 'multiple_choice',
        question_text: questionText.trim(),
        option_a: options['A'],
        option_b: options['B'],
        option_c: options['C'],
        option_d: options['D'],
        correct_option: answer.toLowerCase(),
        difficulty: 'medium',
        metadata
      };
      
      // Add option_e only if it exists
      if (options['E']) {
        parsedQuestion.option_e = options['E'];
      }
      
      questions.push(parsedQuestion);
      
      stats.validQuestions++;
    } catch (error) {
      console.error("Error parsing block:", error, "Block:", block.substring(0, 100) + "...");
      const reason = 'Parser error: ' + (error instanceof Error ? error.message : 'Unknown error');
      stats.skippedReasons[reason] = (stats.skippedReasons[reason] || 0) + 1;
      stats.skippedRows.push({ rowIndex: blockIndex, reason });
      stats.invalidRows++;
    }
  });
  
  console.log("Parsed questions:", questions.length);
  console.log("Parse stats:", stats);
  
  return { questions, stats };
};
